import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import { useNavigate, useParams } from "react-router-dom";

import { toast } from "react-toastify";

import {
  approveDoctor,
  getEclinicDoctorById,
  rejectDoctor,
} from "./EclinicService";

const EclinicDoctorDetails = () => {
  const { id } = useParams();

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);

  const [doctor, setDoctor] = useState(null);

  const [remarks, setRemarks] = useState("");

  const fetchDoctorDetails = async () => {
    try {
      setLoading(true);

      const response = await getEclinicDoctorById(id);

      setDoctor(response?.data?.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to fetch doctor details",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDoctorDetails();
  }, []);

  const handleApprove = async () => {
    try {
      setActionLoading(true);

      await approveDoctor(id, {
        remarks,
      });

      toast.success("Doctor approved successfully");

      fetchDoctorDetails();
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setActionLoading(true);

      await rejectDoctor(id, {
        remarks,
      });

      toast.success("Doctor rejected successfully");

      fetchDoctorDetails();
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return "success";

      case "Rejected":
        return "error";

      default:
        return "warning";
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      p={3}
      sx={{
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      {/* Header */}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/eclinic")}
          >
            Back
          </Button>

          <Typography variant="h5" fontWeight={700}>
            Doctor Registration Details
          </Typography>
        </Box>

        <Chip
          label={doctor?.status}
          color={getStatusColor(doctor?.status)}
          sx={{
            fontWeight: 600,
            fontSize: "14px",
            px: 1,
          }}
        />
      </Box>

      <Grid container spacing={3}>
        {/* Basic Information */}

        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: "18px",
              p: 3,
              height: "100%",
            }}
          >
            <Typography variant="h6" fontWeight={700} mb={2}>
              Basic Information
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <InfoItem label="Full Name" value={doctor?.fullName} />

            <InfoItem label="Mobile Number" value={doctor?.mobileNumber} />

            <InfoItem label="Email Address" value={doctor?.email} />

            <InfoItem label="State" value={doctor?.state} />

            <InfoItem label="City" value={doctor?.city} />
          </Card>
        </Grid>

        {/* Professional Information */}

        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: "18px",
              p: 3,
              height: "100%",
            }}
          >
            <Typography variant="h6" fontWeight={700} mb={2}>
              Professional Information
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <InfoItem label="Programme Type" value={doctor?.programmeType} />

            <InfoItem label="Qualification" value={doctor?.qualification} />

            <InfoItem label="HPR ID" value={doctor?.hprId} />

            <InfoItem label="HFR ID" value={doctor?.hfrId} />

            <InfoItem
              label="Registration Number"
              value={doctor?.registrationNumber}
            />

            <InfoItem
              label="Experience"
              value={`${doctor?.experience} Years`}
            />
          </Card>
        </Grid>

        {/* Verification Section */}

        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: "18px",
              p: 3,
            }}
          >
            <Typography variant="h6" fontWeight={700} mb={2}>
              Verification Information
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <InfoItem
                  label="Registration ID"
                  value={doctor?.registrationId}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <InfoItem
                  label="Registration Date & Time"
                  value={new Date(doctor?.createdAt).toLocaleString()}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <InfoItem label="Current Status" value={doctor?.status} />
              </Grid>
            </Grid>

            {doctor?.resubmissionCount > 0 && (
              <>
                <Grid item xs={12} md={4}>
                  <InfoItem 
                    label="Resubmission Count" 
                    value={doctor?.resubmissionCount} 
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <InfoItem 
                    label="Last Resubmitted" 
                    value={new Date(doctor?.resubmittedAt).toLocaleString()} 
                  />
                </Grid>
              </>
            )}

            <Box mt={3}>
              <Typography fontWeight={600} mb={1}>
                Admin Remarks
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Enter admin remarks..."
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Box>

            {/* Action Buttons */}

            {doctor?.status === "Pending" && (
              <Box mt={4} display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  startIcon={<CheckCircleIcon />}
                  disabled={actionLoading}
                  onClick={handleApprove}
                  sx={{
                    borderRadius: "10px",
                    px: 4,
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  {actionLoading ? "Processing..." : "Approve Registration"}
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  size="large"
                  startIcon={<CancelIcon />}
                  disabled={actionLoading}
                  onClick={handleReject}
                  sx={{
                    borderRadius: "10px",
                    px: 4,
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  {actionLoading ? "Processing..." : "Reject Registration"}
                </Button>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const InfoItem = ({ label, value }) => {
  return (
    <Box mb={2.5}>
      <Typography fontSize="13px" color="#6b7280" mb={0.5}>
        {label}
      </Typography>

      <Typography fontWeight={600} color="#111827">
        {value || "-"}
      </Typography>
    </Box>
  );
};

export default EclinicDoctorDetails;
